/**
 * Chart colour and the shared ECharts option skeleton (SPEC §9.2, §9.4).
 *
 * "Chart palettes derive from the active department accent plus a fixed
 * neutral sequence, so every report in a department looks like it belongs
 * there." That is the whole idea: the first series is the department's accent,
 * and the rest step through one neutral sequence used everywhere. A report
 * does not choose its own colours unless a `SeriesSpec.color` says so
 * explicitly.
 *
 * Semantic colours are not in this sequence and never enter it. Positive and
 * negative variance mean one thing across the product; reusing an accent for
 * them would make "good" look like "this is the sales department".
 */

import type { EChartsOption } from 'echarts'
import type { TooltipOption } from 'echarts/types/dist/shared'

import { formatAxisValue, formatCell } from './format'
import type { Cell, ColumnSpec, SeriesSpec } from './types'

/**
 * The seed department accents (§9.2).
 *
 * **A fallback, not the source of truth.** Departments are admin-managed (M8):
 * the live accent is served on `/api/departments` and `/api/me` and threaded
 * into the charts, so a recolour reaches them. This map only stands in for the
 * moment before that data has loaded, or for a key it does not carry — which is
 * why the values match the initial migration seed. The charts never read it;
 * `Root`/`Report` do, and only as a fallback behind the served value.
 */
export const DEPARTMENT_ACCENTS: Readonly<Record<string, string>> = {
  operations: '#0F6E6E',
  supply_chain: '#8A5A2B',
  sales: '#1E5FA8',
  finance: '#5B3E8E',
}

/**
 * The seed accent for a department key, or ink for an unknown one — never a
 * fifth colour nobody chose. Use only when the served accent is not yet
 * available; prefer the accent from `/api/me` or `/api/departments`.
 */
export function departmentAccent(department: string | undefined): string {
  return (department !== undefined ? DEPARTMENT_ACCENTS[department] : undefined) ?? '#14171A'
}

/**
 * The neutral sequence that follows the accent. Muted on purpose — the accent
 * carries the department, and a second saturated hue next to it just competes.
 */
const NEUTRAL_SEQUENCE: readonly string[] = [
  '#7A8590',
  '#B08968',
  '#4F7A8C',
  '#9A8AA8',
  '#6E8B6E',
  '#A8846A',
]

export const POSITIVE = '#1B7A4B'
export const NEGATIVE = '#B23A2E'

/**
 * Colours for `count` series given the department's accent: accent first, then
 * the neutral sequence. The accent is the *served* value (§9.2, M8) — the
 * caller resolves it from `/api/me` or `/api/departments` — so a recolour in
 * the admin panel reaches the chart without touching this file.
 */
export function seriesPalette(accent: string, count: number): string[] {
  return Array.from({ length: count }, (_unused, index) => {
    if (index === 0) return accent
    // The sequence wraps rather than running out, so a report with more series
    // than colours repeats rather than rendering an undefined stroke.
    return NEUTRAL_SEQUENCE[(index - 1) % NEUTRAL_SEQUENCE.length] ?? accent
  })
}

/** One series' colour: an explicit manifest override, else its palette slot. */
export function seriesColor(series: SeriesSpec, accent: string, index: number): string {
  return series.color ?? seriesPalette(accent, index + 1)[index] ?? accent
}

/**
 * Chart chrome colours, read from the CSS custom properties at render time.
 *
 * A canvas cannot inherit a CSS variable, so without this the axis labels and
 * grid lines would be baked to their light-mode values and glare on a dark
 * surface. Reading the computed style keeps `index.css` the single source of
 * the palette, including its `prefers-color-scheme` override.
 *
 * The fallbacks are the light values, for a non-DOM environment such as a unit
 * test — never a third set of colours invented here.
 */
function themeColor(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value === '' ? fallback : value
}

const inkMuted = (): string => themeColor('--color-ink-muted', '#5C6570')
const rule = (): string => themeColor('--color-rule', '#E3E5E4')

/**
 * The parts of an option object every adapter shares.
 *
 * `animation` respects `prefers-reduced-motion`: §9.2 allows chart entry
 * animation, and the media query is the one thing that overrides it.
 */
export function baseOption(): EChartsOption {
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  return {
    animation: !reducedMotion,
    animationDuration: 150,
    // `bottom` leaves room for the legend, which sits below the axis labels.
    // `containLabel` alone does not account for it, and the two overlap.
    grid: { left: 8, right: 8, top: 16, bottom: 32, containLabel: true },
    textStyle: { fontFamily: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif' },
    tooltip: baseTooltip(),
    legend: {
      bottom: 0,
      icon: 'roundRect',
      itemHeight: 8,
      itemWidth: 8,
      textStyle: { color: inkMuted(), fontSize: 12 },
    },
  }
}

/**
 * The shared tooltip, as its own object.
 *
 * Separate from `baseOption` because `EChartsOption['tooltip']` is a
 * single-or-array union, and spreading that at an adapter's call site would
 * spread an array's indices if it ever were one.
 */
export function baseTooltip(): TooltipOption {
  return {
    trigger: 'axis',
    // Tabular figures in the tooltip too — a number that reflows as the cursor
    // moves is the same failure as a column that does not align.
    textStyle: { fontFamily: 'IBM Plex Mono, ui-monospace, monospace' },
    axisPointer: { type: 'line', lineStyle: { color: rule() } },
  }
}

/** Axis label style shared by every adapter. Mono, because axes carry numbers. */
export function axisLabel(): { color: string; fontFamily: string; fontSize: number } {
  return {
    color: inkMuted(),
    fontFamily: 'IBM Plex Mono, ui-monospace, monospace',
    fontSize: 12,
  }
}

export function axisLine(): { lineStyle: { color: string } } {
  return { lineStyle: { color: rule() } }
}

/**
 * Grid lines, deliberately fainter than the axis itself. A split line as
 * strong as the data is a chart arguing with itself.
 */
export function splitLine(): { lineStyle: { color: string; opacity: number } } {
  return { lineStyle: { color: rule(), opacity: 0.6 } }
}

/** Column lookup by key, for adapters resolving a `SeriesSpec.column`. */
export function columnIndex(columns: readonly ColumnSpec[], key: string): number {
  return columns.findIndex((column) => column.key === key)
}

/** A column's values down a positional row set (§8.6). */
export function columnValues(
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
  key: string,
): Cell[] {
  const index = columnIndex(columns, key)
  if (index === -1) return []
  return rows.map((row) => row[index] ?? null)
}

/**
 * A tooltip formatter bound to the manifest's own column definitions, so a
 * currency in a tooltip matches the same currency in the table (§9.4).
 */
export function axisTooltipFormatter(
  xColumn: ColumnSpec,
  seriesColumns: readonly ColumnSpec[],
): (params: unknown) => string {
  return (params: unknown): string => {
    const points = (Array.isArray(params) ? params : [params]) as {
      axisValue?: unknown
      seriesName?: string
      value?: unknown
      marker?: string
    }[]
    if (points.length === 0) return ''

    const header = formatCell(xColumn, (points[0]?.axisValue ?? null) as Cell)
    const lines = points.map((point) => {
      const column = seriesColumns.find((candidate) => candidate.label === point.seriesName)
      const rendered =
        column === undefined
          ? String(point.value)
          : formatCell(column, (point.value ?? null) as Cell)
      return `${point.marker ?? ''}${point.seriesName ?? ''}: ${rendered}`
    })
    return [header, ...lines].join('<br/>')
  }
}

/** An axis label formatter for one column. */
export function axisLabelFormatter(column: ColumnSpec): (value: unknown) => string {
  return (value: unknown): string => formatAxisValue(column, value as Cell)
}
